const KEY = 'timetrack.server'

/**
 * Запущены ли мы в десктопном приложении. Там адрес сервера надо знать явно.
 *
 * Определять по протоколу мало: Tauri отдаёт страницу по-разному на разных
 * системах — `tauri://localhost` на macOS и Linux, но `http://tauri.localhost`
 * на Windows. По второму варианту протокол обычный http, и проверка на
 * `tauri:`/`file:` десктоп не узнавала: приложение считало себя веб-версией
 * и слало запросы «на тот же домен», то есть в никуда.
 */
export const isDesktop =
  typeof window !== 'undefined' &&
  ('__TAURI_INTERNALS__' in window ||
    '__TAURI__' in window ||
    window.location.protocol === 'tauri:' ||
    window.location.protocol === 'file:' ||
    window.location.hostname === 'tauri.localhost')

/** Адрес, вшитый в сборку. Для веб-версии пусто: API на том же домене. */
const BUILT_IN = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

/**
 * Куда обращаться за данными.
 *
 * Порядок: сохранённый пользователем адрес -> вшитый в сборку -> для десктопа
 * локальный сервер. Настройка хранится у каждого на своей машине: офисный
 * сервер может переехать на другой адрес, и переустанавливать программу
 * ради этого никто не должен.
 */
export function getServerUrl(): string {
  const saved = (() => {
    try {
      return localStorage.getItem(KEY)
    } catch {
      return null
    }
  })()
  if (saved) return saved.replace(/\/+$/, '')
  if (BUILT_IN) return BUILT_IN.replace(/\/+$/, '')
  return isDesktop ? 'http://127.0.0.1:8000' : ''
}

export function setServerUrl(url: string) {
  const clean = url.trim().replace(/\/+$/, '')
  try {
    if (clean) localStorage.setItem(KEY, clean)
    else localStorage.removeItem(KEY)
  } catch {
    /* приватный режим — просто не сохраняем */
  }
}

/** Настроен ли адрес: на десктопе без него ничего не заработает. */
export function needsServerSetup(): boolean {
  return isDesktop && !getServerUrl()
}

/** Живой ли сервер по этому адресу. Возвращает текст ошибки или null. */
export async function checkServer(url: string): Promise<string | null> {
  const base = url.trim().replace(/\/+$/, '')
  if (!base) return 'Укажите адрес сервера'
  if (!/^https?:\/\//i.test(base)) return 'Адрес должен начинаться с http:// или https://'
  try {
    const res = await fetch(`${base}/api/health`, { method: 'GET' })
    if (!res.ok) return `Сервер ответил ${res.status}`
    const body = await res.json()
    return body?.status === 'ok' ? null : 'По этому адресу отвечает не та программа'
  } catch {
    return 'Сервер не отвечает. Проверьте адрес и что компьютер в той же сети.'
  }
}
